import { useEffect, useState, useCallback } from "react";
import { api, formatApiError } from "@/lib/kfmApi";
import { Loader2, Facebook, RefreshCw, Send, X, Check, Bot, Plug, HelpCircle, ChevronDown } from "lucide-react";
import { toast } from "sonner";

const STATUS_TABS = [
  ["held", "Awaiting approval"],
  ["replied", "Replied"],
  ["skipped", "Skipped"],
  ["", "All"],
];

const STATUS_STYLES = {
  held: "bg-amber-500/10 text-amber-400",
  replied: "bg-emerald-500/10 text-emerald-400",
  skipped: "bg-[#141B29] text-slate-400",
  pending: "bg-blue-500/10 text-blue-400",
  error: "bg-red-500/10 text-red-400",
};

function fmt(d) {
  if (!d) return "";
  try { return new Date(d).toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }); }
  catch { return ""; }
}

export default function FacebookComments() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pageId, setPageId] = useState("");
  const [token, setToken] = useState("");
  const [connecting, setConnecting] = useState(false);
  const [polling, setPolling] = useState(false);
  const [showHelp, setShowHelp] = useState(false);

  const [filter, setFilter] = useState("held");
  const [comments, setComments] = useState([]);
  const [loadingComments, setLoadingComments] = useState(false);
  const [drafts, setDrafts] = useState({});
  const [busyId, setBusyId] = useState("");

  const loadStatus = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/social/meta/status");
      setStatus(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Couldn't load Facebook status");
    }
    setLoading(false);
  }, []);

  const loadComments = useCallback(async () => {
    setLoadingComments(true);
    try {
      const { data } = await api.get(`/admin/social/meta/comments${filter ? `?status=${filter}` : ""}`);
      setComments(data.comments || []);
      const d = {};
      (data.comments || []).forEach((c) => { d[c.id] = c.draft_reply || ""; });
      setDrafts(d);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Couldn't load comments");
    }
    setLoadingComments(false);
  }, [filter]);

  useEffect(() => { loadStatus(); }, [loadStatus]);
  useEffect(() => { if (status?.connected) loadComments(); }, [status?.connected, filter, loadComments]);

  const connect = async () => {
    if (!pageId.trim() || !token.trim()) { toast.error("Enter both the Page ID and the Page access token"); return; }
    setConnecting(true);
    try {
      const { data } = await api.put("/admin/social/meta/config", {
        page_id: pageId.trim(), page_access_token: token.trim(),
      });
      toast.success(data.message || "Connected to Facebook");
      setToken(""); setPageId("");
      await loadStatus();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Couldn't connect");
    }
    setConnecting(false);
  };

  const disconnect = async () => {
    try {
      await api.post("/admin/social/meta/disconnect");
      toast.success("Disconnected");
      await loadStatus();
    } catch { toast.error("Couldn't disconnect"); }
  };

  const toggleAutonomy = async () => {
    try {
      const { data } = await api.put("/admin/social/meta/autonomy", { enabled: !status.autonomy_enabled });
      setStatus((s) => ({ ...s, autonomy_enabled: data.autonomy_enabled }));
      toast.success(data.autonomy_enabled
        ? "Autonomy ON — Shee will reply to concierge questions automatically"
        : "Autonomy OFF — Shee will hold every reply for your approval");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Couldn't update"); }
  };

  const pollNow = async () => {
    setPolling(true);
    try {
      const { data } = await api.post("/admin/social/meta/poll");
      toast.success(`Checked ${data.posts} posts · ${data.new} new (${data.replied} replied, ${data.held} held, ${data.skipped} skipped)`);
      await loadStatus();
      await loadComments();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Couldn't reach Facebook");
    }
    setPolling(false);
  };

  const approve = async (cid) => {
    const message = (drafts[cid] || "").trim();
    if (!message) { toast.error("Nothing to post"); return; }
    setBusyId(cid);
    try {
      await api.post(`/admin/social/meta/comments/${encodeURIComponent(cid)}/reply`, { message });
      toast.success("Reply posted to Facebook");
      await loadStatus();
      await loadComments();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Couldn't post the reply");
    }
    setBusyId("");
  };

  const skip = async (cid) => {
    setBusyId(cid);
    try {
      await api.post(`/admin/social/meta/comments/${encodeURIComponent(cid)}/skip`);
      await loadStatus();
      await loadComments();
    } catch { toast.error("Couldn't skip"); }
    setBusyId("");
  };

  if (loading) {
    return <div className="flex items-center gap-2 text-slate-500 py-12"><Loader2 className="w-5 h-5 animate-spin" /> Loading…</div>;
  }

  return (
    <div data-testid="facebook-comments" className="space-y-6">
      {/* Connection */}
      <div className="bg-[#0E131F] rounded-2xl border border-[#1E273D] p-6">
        <div className="flex items-center gap-2 text-white font-medium">
          <Facebook className="w-5 h-5 text-[#1877F2]" /> Facebook Page
        </div>

        {!status?.connected ? (
          <div className="mt-4 space-y-3">
            <p className="text-sm text-slate-400">
              Connect your Facebook Page so Shee can read comments and reply as your concierge.
            </p>
            <input
              data-testid="meta-page-id"
              value={pageId}
              onChange={(e) => setPageId(e.target.value)}
              placeholder="Page ID (numeric)"
              className="w-full rounded-xl border border-[#29344D] px-4 py-2.5 text-sm focus:outline-none focus:border-[#00E5FF]"
            />
            <textarea
              data-testid="meta-token"
              value={token}
              onChange={(e) => setToken(e.target.value)}
              placeholder="Long-lived Page access token"
              rows={3}
              className="w-full rounded-xl border border-[#29344D] px-4 py-2.5 text-sm font-mono focus:outline-none focus:border-[#00E5FF]"
            />
            <button
              data-testid="meta-connect-btn"
              onClick={connect}
              disabled={connecting}
              className="inline-flex items-center gap-2 rounded-full bg-[#00E5FF] hover:bg-[#00B8CC] text-[#07090E] px-5 py-2.5 text-sm font-medium disabled:opacity-60"
            >
              {connecting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plug className="w-4 h-4" />} Connect Page
            </button>

            <button
              onClick={() => setShowHelp((v) => !v)}
              className="flex items-center gap-1.5 text-xs text-[#00E5FF] mt-1"
              data-testid="meta-help-toggle"
            >
              <HelpCircle className="w-3.5 h-3.5" /> How do I get a Page access token?
              <ChevronDown className={`w-3.5 h-3.5 transition-transform ${showHelp ? "rotate-180" : ""}`} />
            </button>
            {showHelp && (
              <ol className="text-xs text-slate-400 leading-relaxed list-decimal ml-4 space-y-1 mt-1 bg-[#0B0F16] rounded-xl p-4">
                <li>Go to <span className="font-medium">developers.facebook.com</span> → create a <span className="font-medium">Business</span> app.</li>
                <li>Add <span className="font-medium">Facebook Login for Business</span>. Under Permissions request <span className="font-mono">pages_show_list</span>, <span className="font-mono">pages_read_engagement</span> and <span className="font-mono">pages_manage_engagement</span>.</li>
                <li>Open the <span className="font-medium">Graph API Explorer</span>, select your app and Page, and generate a <span className="font-medium">User token</span> with those permissions.</li>
                <li>Exchange it for a <span className="font-medium">long-lived</span> token, then call <span className="font-mono">/me/accounts</span> to get your Page&rsquo;s <span className="font-medium">id</span> and <span className="font-medium">access_token</span>.</li>
                <li>Paste that Page <span className="font-medium">id</span> and <span className="font-medium">access_token</span> above. (Long-lived Page tokens generally don&rsquo;t expire.)</li>
              </ol>
            )}
          </div>
        ) : (
          <div className="mt-4 space-y-4">
            <div className="flex items-center justify-between flex-wrap gap-3">
              <div className="text-sm text-slate-400">
                <span className="font-medium text-white">{status.page_name || "Connected"}</span>
                <span className="text-slate-500"> · token {status.token_masked}</span>
                {status.token_valid === false && <span className="text-red-500 ml-2">token invalid — reconnect</span>}
                {status.last_poll_at && <div className="text-xs text-slate-500 mt-0.5">Last checked {fmt(status.last_poll_at)}</div>}
              </div>
              <div className="flex items-center gap-2">
                <button
                  data-testid="meta-poll-btn"
                  onClick={pollNow}
                  disabled={polling}
                  className="inline-flex items-center gap-2 rounded-full border border-[#29344D] hover:border-[#00E5FF] text-slate-300 px-4 py-2 text-sm disabled:opacity-60"
                >
                  {polling ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />} Check now
                </button>
                <button onClick={disconnect} data-testid="meta-disconnect-btn" title="Disconnect this Page so you can paste a new Page ID / access token" className="inline-flex items-center gap-2 rounded-full border border-[#29344D] hover:border-red-500/30 hover:text-red-500 text-slate-400 px-4 py-2 text-sm">
                  <Plug className="w-4 h-4" /> Reconnect / change token
                </button>
              </div>
            </div>

            {/* Autonomy switch */}
            <div className="flex items-center justify-between rounded-xl bg-[#0B0F16] p-4">
              <div className="flex items-start gap-3">
                <Bot className="w-5 h-5 text-[#00E5FF] mt-0.5" />
                <div>
                  <div className="text-sm font-medium text-white">Autonomous replies</div>
                  <div className="text-xs text-slate-400 max-w-md">
                    When on, Shee auto-replies to genuine app / sign-up questions within strict guardrails
                    (never debate or preaching). Everything else is held for you.
                  </div>
                </div>
              </div>
              <button
                data-testid="meta-autonomy-toggle"
                onClick={toggleAutonomy}
                className={`relative w-12 h-7 rounded-full transition-colors shrink-0 ${status.autonomy_enabled ? "bg-[#00E5FF]" : "bg-[#3A4763]"}`}
                aria-pressed={status.autonomy_enabled}
              >
                <span className={`absolute top-1 left-1 w-5 h-5 rounded-full bg-[#0E131F] transition-transform ${status.autonomy_enabled ? "translate-x-5" : ""}`} />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Comments */}
      {status?.connected && (
        <div className="bg-[#0E131F] rounded-2xl border border-[#1E273D] p-6">
          <div className="flex gap-2 p-1 bg-[#141B29] rounded-xl w-fit mb-5 overflow-x-auto no-scrollbar">
            {STATUS_TABS.map(([id, label]) => (
              <button
                key={id || "all"}
                data-testid={`meta-filter-${id || "all"}`}
                onClick={() => setFilter(id)}
                className={`shrink-0 px-4 py-1.5 rounded-lg text-sm font-medium transition-colors flex items-center gap-2 ${filter === id ? "bg-[#0E131F] text-[#00E5FF] shadow-sm" : "text-slate-400"}`}
              >
                {label}
                {id && status.counts?.[id] > 0 && (
                  <span className="inline-flex items-center justify-center min-w-[18px] h-4 px-1 rounded-full bg-[#00E5FF] text-[#07090E] text-[10px] font-semibold">{status.counts[id]}</span>
                )}
              </button>
            ))}
          </div>

          {loadingComments ? (
            <div className="flex items-center gap-2 text-slate-500 py-8"><Loader2 className="w-5 h-5 animate-spin" /> Loading…</div>
          ) : comments.length === 0 ? (
            <p className="text-sm text-slate-500 py-8" data-testid="meta-comments-empty">
              Nothing here yet. {filter === "held" ? "Comments awaiting your approval will show up here." : "Hit “Check now” to pull in recent comments."}
            </p>
          ) : (
            <div className="space-y-4" data-testid="meta-comments-list">
              {comments.map((c) => (
                <div key={c.id} data-testid={`meta-comment-${c.id}`} className="rounded-xl border border-[#1E273D] p-4">
                  <div className="flex items-center justify-between gap-2">
                    <div className="text-sm font-medium text-white">{c.from_name}</div>
                    <div className="flex items-center gap-2">
                      {c.category && <span className="text-[11px] text-slate-500">{c.category}</span>}
                      <span className={`text-[11px] px-2 py-0.5 rounded-full font-medium ${STATUS_STYLES[c.status] || "bg-[#141B29] text-slate-400"}`}>{c.status}</span>
                    </div>
                  </div>
                  <p className="text-sm text-slate-400 mt-1.5 whitespace-pre-wrap">{c.message}</p>
                  <div className="text-[11px] text-slate-500 mt-1">{fmt(c.created_time)}{c.is_reply ? " · reply" : ""}</div>

                  {c.status === "held" && (
                    <div className="mt-3 space-y-2">
                      <textarea
                        data-testid={`meta-draft-${c.id}`}
                        value={drafts[c.id] ?? ""}
                        onChange={(e) => setDrafts((d) => ({ ...d, [c.id]: e.target.value }))}
                        rows={2}
                        className="w-full rounded-lg border border-[#29344D] px-3 py-2 text-sm focus:outline-none focus:border-[#00E5FF]"
                      />
                      <div className="flex items-center gap-2">
                        <button
                          data-testid={`meta-approve-${c.id}`}
                          onClick={() => approve(c.id)}
                          disabled={busyId === c.id}
                          className="inline-flex items-center gap-1.5 rounded-full bg-[#00E5FF] hover:bg-[#00B8CC] text-[#07090E] px-4 py-1.5 text-sm font-medium disabled:opacity-60"
                        >
                          {busyId === c.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />} Approve &amp; post
                        </button>
                        <button
                          data-testid={`meta-skip-${c.id}`}
                          onClick={() => skip(c.id)}
                          disabled={busyId === c.id}
                          className="inline-flex items-center gap-1.5 rounded-full border border-[#29344D] hover:border-red-500/30 text-slate-400 px-4 py-1.5 text-sm disabled:opacity-60"
                        >
                          <X className="w-3.5 h-3.5" /> Skip
                        </button>
                      </div>
                    </div>
                  )}
                  {c.status === "replied" && c.replied_text && (
                    <div className="mt-2 flex items-start gap-1.5 text-sm text-emerald-300 bg-emerald-500/10 rounded-lg p-2.5">
                      <Check className="w-4 h-4 mt-0.5 shrink-0" /> <span className="whitespace-pre-wrap">{c.replied_text}</span>
                    </div>
                  )}
                  {c.status === "error" && c.error && <div className="mt-2 text-sm text-red-500">{c.error}</div>}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
